import { useState } from 'react';
import { useReportStore } from '../../stores/reportStore';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { reportsApi } from '../../services/api';
import { COMPARISON_MODELS } from '../../types';
import { Loader2, Play, RotateCcw, CheckCircle, XCircle, Clock } from 'lucide-react';

interface ModelResult {
  model: string;
  label: string;
  status: 'idle' | 'loading' | 'done' | 'error';
  report: string;
  rawReport: string;
  elapsed: number;
  error?: string;
}

export default function ModelComparison() {
  const { transcription } = useReportStore();
  const { preferences } = usePreferencesStore();

  const [results, setResults] = useState<ModelResult[]>(
    COMPARISON_MODELS.map((m) => ({
      model: m.value,
      label: m.label,
      status: 'idle',
      report: '',
      rawReport: '',
      elapsed: 0,
    }))
  );

  const [isRunning, setIsRunning] = useState(false);

  const runComparison = async () => {
    if (!transcription.trim()) return;

    setIsRunning(true);

    // Reset all to loading
    setResults((prev) =>
      prev.map((r) => ({ ...r, status: 'loading' as const, report: '', rawReport: '', elapsed: 0, error: undefined }))
    );

    // Fire all models in parallel
    const promises = COMPARISON_MODELS.map(async (m, idx) => {
      const start = performance.now();
      try {
        const response = await reportsApi.generate({
          transcription,
          model: m.value,
        });
        const elapsed = Math.round((performance.now() - start) / 1000 * 10) / 10;

        setResults((prev) => {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            status: 'done',
            report: response.report,
            rawReport: response.raw_report,
            elapsed,
          };
          return next;
        });
      } catch (err) {
        const elapsed = Math.round((performance.now() - start) / 1000 * 10) / 10;
        setResults((prev) => {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            status: 'error',
            elapsed,
            error: (err as any)?.response?.data?.detail || (err instanceof Error ? err.message : 'Failed'),
          };
          return next;
        });
      }
    });

    await Promise.allSettled(promises);
    setIsRunning(false);
  };

  const hasTranscription = transcription.trim().length > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Model Comparison
        </h2>
        <div className="flex items-center gap-2">
          {isRunning && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 size={16} className="animate-spin" />
              Generating...
            </div>
          )}
          <button
            onClick={runComparison}
            disabled={!hasTranscription || isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {results.some((r) => r.status === 'done' || r.status === 'error') ? (
              <>
                <RotateCcw size={16} />
                Re-run All
              </>
            ) : (
              <>
                <Play size={16} />
                Run Comparison
              </>
            )}
          </button>
        </div>
      </div>

      {!hasTranscription && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
          Dictate or enter a transcription first, then run comparison.
        </div>
      )}

      {/* Model panes - side by side */}
      {hasTranscription && (
        <div className="flex-1 flex gap-3 overflow-hidden min-h-0">
          {results.map((r) => {
            const modelInfo = COMPARISON_MODELS.find((m) => m.value === r.model);
            return (
              <div key={r.model} className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Model header */}
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {r.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {modelInfo?.description}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {r.status === 'loading' && <Loader2 size={14} className="animate-spin text-primary-500" />}
                    {r.status === 'done' && <CheckCircle size={14} className="text-green-500" />}
                    {r.status === 'error' && <XCircle size={14} className="text-red-500" />}
                    {r.status === 'done' && (
                      <span className="flex items-center gap-0.5 text-xs text-gray-500 dark:text-gray-400">
                        <Clock size={12} />
                        {r.elapsed}s
                      </span>
                    )}
                  </div>
                </div>

                {/* Output */}
                <div
                  className={`flex-1 overflow-y-auto rounded-lg border-2 p-3 ${
                    r.status === 'error'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : r.status === 'done'
                      ? 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600'
                      : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700'
                  }`}
                  style={{ fontSize: `${preferences?.font_size || 13}px` }}
                >
                  {r.status === 'idle' && (
                    <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm">
                      Click "Run Comparison" to generate
                    </div>
                  )}
                  {r.status === 'loading' && (
                    <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                      <Loader2 size={24} className="animate-spin" />
                    </div>
                  )}
                  {r.status === 'error' && (
                    <div className="text-red-600 dark:text-red-400 text-sm">
                      {r.error}
                    </div>
                  )}
                  {r.status === 'done' && (
                    <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans leading-relaxed">
                      {r.rawReport}
                    </pre>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
        <span>Showing raw model output (plain text, before HTML conversion)</span>
      </div>
    </div>
  );
}
